// src/components/InspectSightModal.jsx
import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import "./css/InspectSightModal.css";

export default function InspectSightModal({
    open = false,
    onClose = () => { },
    sight = null,            // sight object from trips/{tripId}/sights snapshot
    db = null,               // firestore db instance
    selectedTrip = null,     // current trip object (needs trip_id)
    user = null,             // current user object
    setToast = null,         // optional toast setter fn
    onSaved = null,          // optional callback(updatedSight)
    onDeleted = null,        // optional callback(deletedSightId)
    useFirebase = Boolean && true, // optional flag if caller wants to override
}) {
    const [localSight, setLocalSight] = useState(sight);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setLocalSight(sight);
        setSaving(false);
        setDeleting(false);
    }, [sight, open]);

    if (!open) return null;

    // prefer any of the common place id fields you store
    const placeId = localSight?.placeId || localSight?.place_id || localSight?.place_id_string || null;

    // more robust coord detection: check both lat and lng explicitly (allow 0)
    const lat = localSight?.location?.lat ?? localSight?.location?.latitude ?? null;
    const lng = localSight?.location?.lng ?? localSight?.location?.longitude ?? null;
    const hasCoords = lat != null && lng != null;
    const isOwner = Boolean(user && selectedTrip && (user.uid === selectedTrip.ownerId || user.uid === selectedTrip.owner_id));

    async function saveMode() {
        if (!localSight) return;
        const newMode = (localSight.mode || localSight.status || "planned").toString();
        if (!db || !selectedTrip?.trip_id) {
            // local only update
            setLocalSight((s) => s ? { ...s, mode: newMode } : s);
            onSaved && onSaved({ ...localSight, mode: newMode });
            setToast && setToast({ msg: `Marked ${localSight.name} as ${newMode}`, type: "success" });
            setTimeout(() => setToast && setToast(null), 1200);
            return;
        }

        setSaving(true);
        try {
            const dr = doc(db, "trips", selectedTrip.trip_id, "sights", localSight.id);
            await updateDoc(dr, { mode: newMode, updatedAt: new Date().toISOString() });
            setLocalSight((s) => s ? { ...s, mode: newMode } : s);
            onSaved && onSaved({ ...localSight, mode: newMode });
            setToast && setToast({ msg: `${localSight.name} updated`, type: "success" });
        } catch (err) {
            console.error("InspectSightModal.saveMode:", err);
            setToast && setToast({ msg: "Failed to update sight", type: "warning" });
        } finally {
            setSaving(false);
            setTimeout(() => setToast && setToast(null), 1200);
        }
    }

    // moved delete logic from Travel.js into modal as requested
    async function deleteSavedSight(ev) {
        try {
            if (ev && typeof ev.stopPropagation === "function") ev.stopPropagation();
            if (!localSight) return;

            const canManage = Boolean(user && selectedTrip && (user.uid === selectedTrip.ownerId || user.uid === selectedTrip.owner_id));
            if (!canManage) {
                setToast && setToast({ msg: "Only the trip owner can remove sights", type: "warning" });
                setTimeout(() => setToast && setToast(null), 1400);
                return;
            }

            // confirm with user (you can replace with a prettier modal if desired)
            if (!window.confirm(`Remove "${localSight.name}" from this trip? This will delete it permanently.`)) return;

            // If not using Firebase or db missing, remove locally
            if (!useFirebase || !db || !selectedTrip?.trip_id) {
                onDeleted && onDeleted(localSight.id);
                onClose();
                setToast && setToast({ msg: `${localSight.name} removed (local)`, type: "success" });
                setTimeout(() => setToast && setToast(null), 1300);
                return;
            }

            setDeleting(true);
            const tripId = selectedTrip.trip_id;
            try {
                await deleteDoc(doc(db, "trips", tripId, "sights", localSight.id));
                onDeleted && onDeleted(localSight.id);
                onClose();
                setToast && setToast({ msg: `${localSight.name} removed`, type: "success" });
            } catch (err) {
                console.error("InspectSightModal.deleteSavedSight firestore error:", err);
                // still notify caller to remove locally to avoid stale UI
                onDeleted && onDeleted(localSight.id);
                onClose();
                setToast && setToast({ msg: "Removed locally (failed to persist)", type: "warning" });
            } finally {
                setDeleting(false);
                setTimeout(() => setToast && setToast(null), 1500);
            }
        } catch (err) {
            console.error("deleteSavedSight unexpected error:", err);
            setToast && setToast({ msg: "Failed to remove sight", type: "warning" });
            setTimeout(() => setToast && setToast(null), 1400);
        }
    }

    const mapSrc = hasCoords
        ? `https://maps.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}&z=15&output=embed`
        : (placeId
            ? `https://www.google.com/maps?q=place_id:${encodeURIComponent(placeId)}&output=embed`
            : null
        );

    return (
        <Modal title={localSight?.name || "Place details"} onClose={() => onClose()}>
            <div style={{ minWidth: 320, maxWidth: 900, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ height: 320, borderRadius: 6, overflow: "hidden", border: "1px solid #eee" }}>
                    {mapSrc ? (
                        <iframe
                            title={`map-${localSight?.id || placeId}`}
                            src={mapSrc}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    ) : (
                        <div style={{ padding: 18 }} className="muted">Map not available for this sight.</div>
                    )}
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ margin: "4px 0 6px 0" }}>{localSight?.name}</h3>
                        <div className="muted" style={{ fontSize: 13 }}>
                            {localSight?.category || ""}
                            {localSight?.location?.address ? ` · ${localSight.location.address}` : ""}
                        </div>

                        {localSight?.raw && (
                            <div style={{ marginTop: 10, fontSize: 13 }}>
                                {localSight.raw.description && <div style={{ marginBottom: 8 }}>{localSight.raw.description}</div>}
                                {localSight.raw.formatted_phone_number && <div className="muted">Phone: {localSight.raw.formatted_phone_number}</div>}
                                {localSight.raw.website && <div className="muted">Website: <a href={localSight.raw.website} target="_blank" rel="noreferrer">{localSight.raw.website}</a></div>}
                                {localSight.raw.opening_hours?.weekday_text && (
                                    <div style={{ marginTop: 8 }}>
                                        <strong>Opening hours</strong>
                                        <ul style={{ marginTop: 6 }}>
                                            {localSight.raw.opening_hours.weekday_text.map((ln, i) => <li key={i} style={{ fontSize: 13 }}>{ln}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {!localSight?.raw && localSight?.photoUrl && (
                            <div style={{ marginTop: 8 }} className="muted">Photo available. Open Google Maps for more info.</div>
                        )}
                    </div>

                    <div style={{ width: 200, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ fontSize: 12, color: "#666" }}>Visit status</div>
                        <select
                            value={localSight?.mode || localSight?.status || "planned"}
                            onChange={(e) => setLocalSight((s) => s ? { ...s, mode: e.target.value } : s)}
                            style={{ padding: "8px 10px", fontSize: 14 }}
                            disabled={saving}
                        >
                            <option value="planned">Planned</option>
                            <option value="visited">Visited</option>
                        </select>

                        <button
                            className="btn-start"
                            onClick={saveMode}
                            disabled={saving}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>

                        {isOwner && (
                            <button
                                className="btn-danger"
                                onClick={deleteSavedSight}
                                disabled={deleting}
                                style={{ marginTop: 6 }}
                            >
                                {deleting ? "Deleting…" : "Delete sight"}
                            </button>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button className="btn-cancel" onClick={() => onClose()}>Close</button>
                </div>
            </div>
        </Modal>
    );
}
